import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CHAT_ANIMATIONS, STAGGER_CONFIG } from '@/utils/chat-animations';
import { useRecommendationDetection } from '@/hooks/ai-agents/functions/core/useRecommendationDetection';
import { useThreadContext } from '@/hooks/ai-agents/functions/core/useThreadContext';
import { ChatRecommendedTaskCard } from './ChatRecommendedTaskCard';
import { ChatRecommendedResearchCard } from './ChatRecommendedResearchCard';
import { ChatRecommendedPageCard } from './ChatRecommendedPageCard';
import { ChatRecommendedPageCardWithContent } from './ChatRecommendedPageCardWithContent';
import { ChatRecommendedImageCard } from './ChatRecommendedImageCard';
import { ChatRecommendedProjectCard } from './ChatRecommendedProjectCard';
import { MessageDisplay } from '@/hooks/ai-agents/types/chat-hook-types';
import { cn } from '@/lib/utils';

interface ChatRecommendationsDisplayProps {
  message: MessageDisplay;
  className?: string;
}

/**
 * Unified component that displays all recommendations (tasks, research, and pages) from an AI message
 * Supports both project-linked chats (thread context) and global chats (extracted IDs)
 */
export const ChatRecommendationsDisplay: React.FC<ChatRecommendationsDisplayProps> = ({
  message,
  className
}) => {
  // Only process assistant messages
  const isAssistant = message.role === 'assistant';
  
  // First check if we have parsed response data in metadata (from dual-format responses)
  const parsedResponse = message.metadata?.parsedResponse;
  const originalResponse = message.metadata?.originalResponse;
  
  // Use parsed data if available, otherwise fall back to parsing content
  const contentToParse = parsedResponse?.hasRecommendation && parsedResponse.structuredContent 
    ? parsedResponse.structuredContent 
    : (originalResponse || message.content || '');
  
  // Detect all recommendations in the message
  const { hasRecommendations, tasks, research, pages, images, projects, hasContentGeneration, reason } = useRecommendationDetection(
    isAssistant ? contentToParse : ''
  );
  
  // Get thread context to determine if we can save tasks
  const { data: threadContext } = useThreadContext(
    hasRecommendations ? message.threadId : null
  );
  
  // Don't render if not an assistant message or no recommendations
  if (!isAssistant || !hasRecommendations) {
    return null;
  }

  // 🌍 GLOBAL SMART DETECTION: Always show if detected (no project dependencies)
  const showTasks = tasks.length > 0; // Always show if detected
  const showResearch = research.length > 0; // Always show if detected
  const showPages = pages.length > 0; // Always show if detected
  const showImages = images.length > 0; // Always show if detected
  const showProjects = projects.length > 0; // Projects can always be created
  
  // Don't render if no valid recommendations to show
  if (!showTasks && !showResearch && !showPages && !showImages && !showProjects) {
    return null;
  }

  // Calculate total number of cards for staggering
  const totalCards = tasks.length + research.length + pages.length + images.length + projects.length;
  let cardIndex = 0;

  const getStaggerDelay = () => {
    const delay = cardIndex * STAGGER_CONFIG.recommendationCards.staggerChildren;
    cardIndex++;
    return delay;
  };

  return (
    <AnimatePresence>
      <motion.div
        variants={CHAT_ANIMATIONS.recommendationsContainer}
        initial="initial"
        animate="animate"
        exit="exit"
        className={cn("space-y-2", className)}
      >
        {/* Task Recommendations */}
        {showTasks && tasks.map((task, index) => (
          <motion.div
            key={`task-${index}`}
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ 
              opacity: 1, 
              y: 0, 
              scale: 1,
              transition: { 
                duration: 0.3, 
                ease: 'easeOut',
                delay: getStaggerDelay()
              }
            }}
            exit={{ 
              opacity: 0, 
              y: -10, 
              scale: 0.95,
              transition: { 
                duration: 0.2, 
                ease: 'easeOut'
              }
            }}
          >
            <ChatRecommendedTaskCard
              task={task}
              message={message}
              type="task"
            />
          </motion.div>
        ))}
        
        {/* Research Recommendations */}
        {showResearch && research.map((researchItem, index) => (
          <motion.div
            key={`research-${index}`}
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ 
              opacity: 1, 
              y: 0, 
              scale: 1,
              transition: { 
                duration: 0.3, 
                ease: 'easeOut',
                delay: getStaggerDelay()
              }
            }}
            exit={{ 
              opacity: 0, 
              y: -10, 
              scale: 0.95,
              transition: { 
                duration: 0.2, 
                ease: 'easeOut'
              }
            }}
          >
            <ChatRecommendedResearchCard
              research={researchItem}
              threadId={message.threadId}
              message={message}
              type="research"
            />
          </motion.div>
        ))}
        
        {/* Page Recommendations */}
        {showPages && pages.map((pageItem, index) => (
          <motion.div
            key={`page-${index}`}
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ 
              opacity: 1, 
              y: 0, 
              scale: 1,
              transition: { 
                duration: 0.3, 
                ease: 'easeOut',
                delay: getStaggerDelay()
              }
            }}
            exit={{ 
              opacity: 0, 
              y: -10, 
              scale: 0.95,
              transition: { 
                duration: 0.2, 
                ease: 'easeOut'
              }
            }}
          >
            {hasContentGeneration ? (
              <ChatRecommendedPageCardWithContent
                page={pageItem}
                threadId={message.threadId}
                message={message}
                type="page"
              />
            ) : (
              <ChatRecommendedPageCard
                page={pageItem}
                threadId={message.threadId}
                message={message}
                type="page"
              />
            )}
          </motion.div>
        ))}
        
        {/* Image Recommendations */}
        {showImages && images.map((imageItem, index) => (
          <motion.div
            key={`image-${index}`}
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ 
              opacity: 1, 
              y: 0, 
              scale: 1,
              transition: { 
                duration: 0.3, 
                ease: 'easeOut',
                delay: getStaggerDelay()
              }
            }}
            exit={{ 
              opacity: 0, 
              y: -10, 
              scale: 0.95,
              transition: { 
                duration: 0.2, 
                ease: 'easeOut'
              }
            }}
          >
            <ChatRecommendedImageCard
              image={imageItem}
              threadId={message.threadId}
              message={message}
              type="image"
            />
          </motion.div>
        ))}
        
        {/* Project Recommendations */}
        {showProjects && projects.map((projectItem, index) => (
          <motion.div
            key={`project-${index}`}
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ 
              opacity: 1, 
              y: 0, 
              scale: 1,
              transition: { 
                duration: 0.3, 
                ease: 'easeOut',
                delay: getStaggerDelay()
              }
            }}
            exit={{ 
              opacity: 0, 
              y: -10, 
              scale: 0.95,
              transition: { 
                duration: 0.2, 
                ease: 'easeOut'
              }
            }}
          >
            <ChatRecommendedProjectCard
              project={projectItem}
              message={message}
              type="project"
            />
          </motion.div>
        ))}
      </motion.div>
    </AnimatePresence>
  );
}; 